// LF360-1810 (task-window extension). Shared defaults + the pure seeding
// logic used when a task criterion's window Selectbox changes to/from
// 'last-n'. Kept outside BehaviorRulesNodeInfo.tsx (and out of the other
// 'today'/'latest'-window seeding sites, which don't need this) so the
// switch-defaulting logic has a single source of truth and is unit-testable
// without a DOM/React environment (this repo's jest config has no jsdom).
export const DEFAULT_LAST_N_PERIOD = 3;
export const DEFAULT_MATCH_MODE: 'all' | 'any' | 'at-least' = 'all';

export interface TaskWindowSwitchInput {
  taskWindow?: string;
  period?: number;
  matchMode?: 'all' | 'any' | 'at-least';
  threshold?: number;
}

export interface TaskWindowSwitchSeed {
  period: number;
  matchMode: 'all' | 'any' | 'at-least' | undefined;
  threshold: number | undefined;
}

/**
 * Computes the {period, matchMode, threshold} to seed on a criterion when
 * its window Selectbox changes to `nextWindow`.
 *
 * - Switching to a single-instance window (today/yesterday/latest) resets
 *   period to 1 and clears matchMode/threshold.
 * - Switching to 'last-n' preserves the existing N/matchMode/threshold only
 *   when the criterion was ALREADY on 'last-n' (`prev.taskWindow`) - a fresh
 *   switch-in from any single-instance window always reseeds
 *   period=DEFAULT_LAST_N_PERIOD + matchMode='all', regardless of `prev.period`
 *   (which is always 1 there, since single-instance windows force it to 1 -
 *   checking `prev.period >= 1` alone can never distinguish "fresh switch-in"
 *   from "already on last-n" and was the LF360-1810 fix-wave-1 bug).
 */
export function seedTaskWindowSwitch(
  prev: TaskWindowSwitchInput,
  nextWindow: string,
): TaskWindowSwitchSeed {
  if (nextWindow !== 'last-n') {
    return { period: 1, matchMode: undefined, threshold: undefined };
  }

  const wasAlreadyLastN = prev.taskWindow === 'last-n';
  const period = (wasAlreadyLastN && prev.period && prev.period >= 1) ? prev.period : DEFAULT_LAST_N_PERIOD;

  return {
    period,
    matchMode: prev.matchMode ?? DEFAULT_MATCH_MODE,
    threshold: prev.threshold,
  };
}
